import { filterClasses } from "@swivl-mono/helpers";
import { BOT_SEQUENCE_MISSED_REASON, BotSequenceDeliveryKey, BotSequenceDeliveryOutcome, BotSequenceScheduledAttemptStatus } from "@swivl-mono/types";
import { DropdownOptionItem, DropdownOptionVariant } from "@swivl-mono/ui";

/**
 * The ledger statuses in lifecycle order — in-flight, sent, then every not-sent shape,
 * simulation last. Drives the filter dropdown and the byStatus rendering order.
 */
export const STATUS_ORDER: BotSequenceScheduledAttemptStatus[] = [
  BotSequenceScheduledAttemptStatus.SCHEDULED,
  BotSequenceScheduledAttemptStatus.DISPATCHED,
  BotSequenceScheduledAttemptStatus.SENDING,
  BotSequenceScheduledAttemptStatus.FIRED,
  BotSequenceScheduledAttemptStatus.INCOMPLETE,
  BotSequenceScheduledAttemptStatus.CANCELLED,
  BotSequenceScheduledAttemptStatus.FAILED,
  BotSequenceScheduledAttemptStatus.SKIPPED_HOLIDAY,
  BotSequenceScheduledAttemptStatus.SKIPPED_SUPERSEDED,
  BotSequenceScheduledAttemptStatus.SKIPPED_SUPPRESSED,
  BotSequenceScheduledAttemptStatus.SKIPPED_STALE,
  BotSequenceScheduledAttemptStatus.SIMULATED
];

export const STATUS_LABELS: Record<BotSequenceScheduledAttemptStatus, string> = {
  [BotSequenceScheduledAttemptStatus.SCHEDULED]: "Scheduled",
  [BotSequenceScheduledAttemptStatus.DISPATCHED]: "Dispatched",
  [BotSequenceScheduledAttemptStatus.SENDING]: "Sending",
  [BotSequenceScheduledAttemptStatus.FIRED]: "Sent",
  [BotSequenceScheduledAttemptStatus.INCOMPLETE]: "Incomplete",
  [BotSequenceScheduledAttemptStatus.CANCELLED]: "Cancelled",
  [BotSequenceScheduledAttemptStatus.FAILED]: "Failed",
  [BotSequenceScheduledAttemptStatus.SKIPPED_HOLIDAY]: "Skipped: holiday",
  [BotSequenceScheduledAttemptStatus.SKIPPED_SUPERSEDED]: "Skipped: superseded",
  [BotSequenceScheduledAttemptStatus.SKIPPED_SUPPRESSED]: "Skipped: suppressed",
  [BotSequenceScheduledAttemptStatus.SKIPPED_STALE]: "Skipped: stale",
  [BotSequenceScheduledAttemptStatus.SIMULATED]: "Simulated"
};

/**
 * Yellow = in flight, teal = sent, orange = blocked at fire time or only partly sent (incomplete), red = an error,
 * grey = an expected skip, purple = simulation mode.
 */
const STATUS_CHIP_CLASSES: Record<BotSequenceScheduledAttemptStatus, string> = {
  [BotSequenceScheduledAttemptStatus.SCHEDULED]: "bg-yellow-50 text-yellow-800",
  [BotSequenceScheduledAttemptStatus.DISPATCHED]: "bg-yellow-50 text-yellow-800",
  [BotSequenceScheduledAttemptStatus.SENDING]: "bg-yellow-50 text-yellow-800",
  [BotSequenceScheduledAttemptStatus.FIRED]: "bg-teal-50 text-teal-800",
  [BotSequenceScheduledAttemptStatus.INCOMPLETE]: "bg-orange-50 text-orange-700",
  [BotSequenceScheduledAttemptStatus.CANCELLED]: "bg-orange-50 text-orange-700",
  [BotSequenceScheduledAttemptStatus.FAILED]: "bg-red-50 text-red-900",
  [BotSequenceScheduledAttemptStatus.SKIPPED_HOLIDAY]: "bg-grey-100 text-grey-700",
  [BotSequenceScheduledAttemptStatus.SKIPPED_SUPERSEDED]: "bg-grey-100 text-grey-700",
  [BotSequenceScheduledAttemptStatus.SKIPPED_SUPPRESSED]: "bg-grey-100 text-grey-700",
  [BotSequenceScheduledAttemptStatus.SKIPPED_STALE]: "bg-grey-100 text-grey-700",
  [BotSequenceScheduledAttemptStatus.SIMULATED]: "bg-purple-50 text-purple-700"
};

interface StatusChipProps {
  status: BotSequenceScheduledAttemptStatus;
  /** The row's not-sent reason; a stale row whose reason is `missed` reads "Skipped: missed" (decision 69). */
  reason?: string | null;
}

export function statusChipLabel(status: BotSequenceScheduledAttemptStatus, reason?: string | null): string {
  return status === BotSequenceScheduledAttemptStatus.SKIPPED_STALE && reason === BOT_SEQUENCE_MISSED_REASON
    ? "Skipped: missed"
    : STATUS_LABELS[status];
}

export function StatusChip({ status, reason }: StatusChipProps): React.ReactElement {
  return (
    <span
      className={filterClasses("rounded-full px-2 py-0.5 text-xs font-medium", STATUS_CHIP_CLASSES[status])}
      title={reason === BOT_SEQUENCE_MISSED_REASON ? "Already past when the tenant was enrolled — never sent" : undefined}
    >
      {statusChipLabel(status, reason)}
    </span>
  );
}

/** "missed · also Days 0, 3" — the reason cell for a missed row that stands for several days. */
export function describeMissedReason(reason: string | null, missedOffsets: number[] | null, stepOffset: number): string | null {
  if (reason !== BOT_SEQUENCE_MISSED_REASON) return reason;
  const others = (missedOffsets ?? []).filter((offset) => offset !== stepOffset);
  return others.length > 0 ? `missed · also Day${others.length === 1 ? "" : "s"} ${others.join(", ")}` : "missed";
}

/**
 * The summary tiles — status families, not single statuses, so the page opens with the
 * question everyone brings ("what went out, what's pending, what didn't go?") rather than
 * the eleven-way taxonomy. `null` statuses = the Total tile (clears the drill).
 */
export const STATUS_TILES: { key: string; label: string; statuses: BotSequenceScheduledAttemptStatus[] | null }[] = [
  { key: "total", label: "Total", statuses: null },
  {
    key: "upcoming",
    label: "Upcoming",
    statuses: [BotSequenceScheduledAttemptStatus.SCHEDULED, BotSequenceScheduledAttemptStatus.DISPATCHED, BotSequenceScheduledAttemptStatus.SENDING]
  },
  { key: "sent", label: "Sent", statuses: [BotSequenceScheduledAttemptStatus.FIRED, BotSequenceScheduledAttemptStatus.INCOMPLETE] },
  {
    key: "notSent",
    label: "Not sent",
    statuses: [
      BotSequenceScheduledAttemptStatus.SKIPPED_HOLIDAY,
      BotSequenceScheduledAttemptStatus.SKIPPED_SUPERSEDED,
      BotSequenceScheduledAttemptStatus.SKIPPED_SUPPRESSED,
      BotSequenceScheduledAttemptStatus.SKIPPED_STALE,
      BotSequenceScheduledAttemptStatus.CANCELLED,
      BotSequenceScheduledAttemptStatus.FAILED
    ]
  },
  { key: "simulated", label: "Simulated", statuses: [BotSequenceScheduledAttemptStatus.SIMULATED] }
];

/** One multi-select "Status" group for the shared FilterDropdown. */
export const STATUS_DROPDOWN_GROUPS: DropdownOptionItem[] = [
  {
    label: "Status",
    value: "status",
    isMultiSelect: true,
    options: STATUS_ORDER.map((status) => ({
      value: status,
      label: STATUS_LABELS[status],
      variant: DropdownOptionVariant.WITH_CHECKBOX
    }))
  }
];

/** The statuses the suppressions page can explain further — its feed's exact status set. */
export const SUPPRESSION_LINKED_STATUSES = new Set<BotSequenceScheduledAttemptStatus>([
  BotSequenceScheduledAttemptStatus.SKIPPED_HOLIDAY,
  BotSequenceScheduledAttemptStatus.SKIPPED_SUPERSEDED,
  BotSequenceScheduledAttemptStatus.SKIPPED_SUPPRESSED,
  BotSequenceScheduledAttemptStatus.SKIPPED_STALE,
  BotSequenceScheduledAttemptStatus.CANCELLED,
  BotSequenceScheduledAttemptStatus.FAILED
]);

// ── What was attempted (decision 65) ───────────────────────────────────────────────

const DELIVERY_ORDER: BotSequenceDeliveryKey[] = ["sms", "voice", "email", "fmsNote"];
const DELIVERY_LABELS: Record<BotSequenceDeliveryKey, string> = { sms: "Text", voice: "Call", email: "Email", fmsNote: "Note" };
const CALL_OUTCOME_LABELS: Record<string, string> = {
  answered: "answered",
  voicemail_left: "voicemail left",
  voicemail_skipped: "machine, no voicemail",
  no_answer: "no answer",
  busy: "busy",
  failed: "failed"
};

function humanizeReason(reason: string): string {
  return reason.replace(/_/g, " ").replace(/:/g, ": ");
}

/** One line per delivery: "Call → +1 917 …: voicemail left, 28 s" — recipient, then how it ended. */
export function describeDelivery(key: BotSequenceDeliveryKey, entry: BotSequenceDeliveryOutcome): string {
  const target = entry.recipient ? ` → ${entry.recipient}` : "";
  let ending: string = entry.status;
  if (key === "voice" && entry.callOutcome) {
    ending = CALL_OUTCOME_LABELS[entry.callOutcome] ?? entry.callOutcome;
    if (entry.callDurationSeconds !== undefined) ending += `, ${entry.callDurationSeconds} s`;
  } else if ((entry.status === "failed" || entry.status === "skipped") && entry.reason) {
    ending = `${entry.status}, ${humanizeReason(entry.reason)}`;
  } else if (entry.status === "simulated") {
    // The Outbound Gate built and recorded it and withheld it from the wire (ENG-3373) — always with its reason.
    ending = `simulated${entry.gateReason ? `, ${humanizeReason(entry.gateReason)}` : ""}`;
  } else if (entry.status === "blocked") {
    ending = "blocked, opted out";
  } else if (entry.status === "sent" && entry.sandboxed) {
    ending = "sent (simulated)";
  }
  if (entry.status === "sent" && entry.consentOverridden) ending += " — sent despite opt-out (legally required)";
  return `${DELIVERY_LABELS[key]}${target}: ${ending}`;
}

interface DeliveriesCellProps {
  deliveries: Partial<Record<BotSequenceDeliveryKey, BotSequenceDeliveryOutcome>> | null;
}

/** The attempt's ledger as staff read it — every delivery, its recipient and its outcome; "—" before the fire seeded it. */
export function DeliveriesCell({ deliveries }: DeliveriesCellProps): React.ReactElement {
  const keys = DELIVERY_ORDER.filter((key) => deliveries?.[key]);
  if (!deliveries || keys.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {keys.map((key) => (
        <span key={key} className="whitespace-nowrap">
          {describeDelivery(key, deliveries[key] as BotSequenceDeliveryOutcome)}
        </span>
      ))}
    </div>
  );
}
